/**
 * Viewing Deck tab — real-time event stream with sidebar cards.
 *
 * Renders the HTML shell immediately (synchronous), then kicks off async work
 * in a setTimeout to follow the same pattern as timeline.ts.
 */

import { esc } from '../../../shared/rendering.js'
import { registerTeardown } from '../../../router.js'
import { fetchRunState, openRunStream, fetchRunEvents } from '../../../api/run.js'
import { renderEventCard } from '../../components/eventCard.js'
import { renderEventList, buildEventCardProps, renderSidebarContent } from './viewingDeck_render.js'
import type { RunOverview, ParsedEvent, RunSSEFrame } from '../../../types/api.js'

// ─── Module State ────────────────────────────────────────────────────────────

let _activeStream: EventSource | null = null
let _liveEvents: ParsedEvent[] = []
let _activeFilter: string | undefined = undefined
let _renderGen = 0

const EVENTS_PAGE_SIZE = 50

// ─── Public Entry Point ──────────────────────────────────────────────────────

/** Render the Viewing Deck tab. Returns HTML immediately; loads data async. */
export function renderViewingDeckTab(data: RunOverview, runId: string): string {
  // Close any previous SSE stream from a prior navigation
  if (_activeStream !== null) {
    _activeStream.close()
    _activeStream = null
  }
  _liveEvents = []
  _activeFilter = undefined
  const gen = ++_renderGen

  // Close stream and invalidate async callbacks when navigating away
  registerTeardown(() => {
    if (_activeStream !== null) {
      _activeStream.close()
      _activeStream = null
    }
    _renderGen++
  })

  const isRunning = data.summary.status === 'running'

  // Defer until after the HTML shell is in the DOM; gen guards against stale callbacks
  setTimeout(() => {
    if (_renderGen !== gen) {
      return
    }
    void loadDeckData(runId, isRunning, gen)
  }, 0)

  return renderShell(data, isRunning)
}

// ─── Shell HTML ──────────────────────────────────────────────────────────────

function renderShell(data: RunOverview, isRunning: boolean): string {
  const liveBadge = isRunning
    ? '<span class="live">LIVE</span>'
    : '<span style="font-family:var(--mono);font-size:11px;color:var(--t3)">Completed</span>'

  // Current stage populated async via obs-side sidebar (from RunState)
  const stage = isRunning ? '<span class="obs-stage" id="obs-stage"></span>' : ''

  return `<div class="obs-bar" id="obs-bar">
    ${liveBadge}
    <span class="obs-cnt" id="obs-cnt">${data.summary.total_events} events</span>
    ${stage}
  </div>
  <div class="tf obs-filters" role="group" aria-label="Filter transcript entries">
    <span class="fp on" role="button" tabindex="0" aria-pressed="true" data-obs-filter="">All</span>
    <span class="fp" role="button" tabindex="0" aria-pressed="false" data-obs-filter="tool">Tools</span>
    <span class="fp" role="button" tabindex="0" aria-pressed="false" data-obs-filter="llm">Messages</span>
    <span class="fp" role="button" tabindex="0" aria-pressed="false" data-obs-filter="stage">Steps</span>
  </div>
  <div class="obs${isRunning ? '' : ' no-tasks'}">
    <div class="obs-main" id="obs-main">
      <div class="loading"><div class="spinner"></div></div>
    </div>
    ${isRunning ? '<div class="obs-side" id="obs-side"><div class="loading"><div class="spinner"></div></div></div>' : ''}
  </div>`
}

// ─── Data Loading ─────────────────────────────────────────────────────────────

async function loadDeckData(runId: string, isRunning: boolean, gen: number): Promise<void> {
  // Fire-and-forget: sidebar is independent of main event list; it has its own null-guard
  void loadSidebar(runId, gen)

  if (isRunning) {
    if (_renderGen !== gen) {
      return
    }
    startLiveStream(runId, gen)
  } else {
    await loadCompletedEvents(runId, gen)
  }

  if (_renderGen !== gen) {
    return
  }
  wireFilterPills(runId, isRunning)
}

async function loadSidebar(runId: string, gen: number): Promise<void> {
  const side = document.getElementById('obs-side')
  if (!side) {
    return
  }
  try {
    const state = await fetchRunState(runId)
    if (_renderGen !== gen) {
      return
    }
    side.innerHTML = renderSidebarContent(state.task_lists, state.files_touched, state.kb_items, {
      current_stage: state.current_stage,
      subagents: state.subagents,
    })
    const stageEl = document.getElementById('obs-stage')
    if (stageEl && state.current_stage) {
      stageEl.textContent = state.current_stage
    }
  } catch (err) {
    if (_renderGen !== gen) {
      return
    }
    side.innerHTML = `<div class="empty">Failed to load sidebar. ${esc(String(err))}</div>`
  }
}

async function loadCompletedEvents(runId: string, gen: number): Promise<void> {
  const container = document.getElementById('obs-main')
  if (!container) {
    return
  }
  try {
    const result = await fetchRunEvents(runId, { page_size: EVENTS_PAGE_SIZE, order: 'desc' })
    if (_renderGen !== gen) {
      return
    }
    _liveEvents = result.data
    container.innerHTML = renderEventList(_liveEvents, _activeFilter)
  } catch (err) {
    if (_renderGen !== gen) {
      return
    }
    container.innerHTML = `<div class="empty">Failed to load events. ${esc(String(err))}</div>`
  }
}

function startLiveStream(runId: string, gen: number): void {
  _activeStream = openRunStream(runId, undefined, (frame: RunSSEFrame) => {
    if (_renderGen !== gen) {
      _activeStream?.close()
      _activeStream = null
      return
    }
    if (frame.type === 'new_event' && frame.event) {
      if (_liveEvents.some((ev) => ev.id === frame.event?.id)) {
        return
      }
      _liveEvents = [frame.event, ..._liveEvents]
      prependEventCard(frame.event)
      updateEventCount(_liveEvents.length)
    } else if (frame.type === 'run_completed') {
      if (_activeStream) {
        _activeStream.close()
        _activeStream = null
      }
      updateStatusBadge(false)
    }
  })
}

// ─── Event Card Rendering ─────────────────────────────────────────────────────

function prependEventCard(ev: ParsedEvent): void {
  const container = document.getElementById('obs-main')
  if (!container) {
    return
  }
  // Clear the initial spinner on first event
  if (container.querySelector('.spinner')) {
    container.innerHTML = ''
  }
  if (_activeFilter !== undefined) {
    const type = ev.event_type
    const kind =
      type === 'tool' || type.includes('Tool')
        ? 'tool'
        : type === 'llm' || type.includes('LLM')
          ? 'llm'
          : type === 'stage' || type.startsWith('Stage')
            ? 'stage'
            : type
    if (kind !== _activeFilter) return
  }
  const card = document.createElement('div')
  card.innerHTML = renderEventCard(buildEventCardProps(ev))
  container.insertBefore(card.firstElementChild ?? card, container.firstChild)
}

// ─── DOM Helpers ──────────────────────────────────────────────────────────────

function updateStatusBadge(isRunning: boolean): void {
  const bar = document.getElementById('obs-bar')
  if (!bar) {
    return
  }
  const badge = bar.querySelector('.live, [style*="Completed"]')
  if (badge) {
    badge.outerHTML = isRunning
      ? '<span class="live">LIVE</span>'
      : '<span style="font-family:var(--mono);font-size:11px;color:var(--t3)">Completed</span>'
  }
}

function updateEventCount(count: number): void {
  const el = document.getElementById('obs-cnt')
  if (el) {
    el.textContent = `${count} events`
  }
}

// ─── Filter Pills ─────────────────────────────────────────────────────────────

function wireFilterPills(runId: string, isRunning: boolean): void {
  const filtersContainer = document.querySelector('.obs-filters')
  if (!filtersContainer) {
    return
  }

  const handlePillActivate = (pill: HTMLElement): void => {
    filtersContainer.querySelectorAll('.fp[data-obs-filter]').forEach((p) => {
      p.classList.remove('on')
      p.setAttribute('aria-pressed', 'false')
    })
    pill.classList.add('on')
    pill.setAttribute('aria-pressed', 'true')
    const value = pill.dataset.obsFilter || undefined
    _activeFilter = value

    const container = document.getElementById('obs-main')
    if (!container) {
      return
    }

    if (isRunning) {
      container.innerHTML = renderEventList(_liveEvents, _activeFilter)
    } else {
      // Completed runs already loaded the first page of events. Filter those
      // immediately instead of flashing a spinner for a second round-trip.
      // The server still supports filtered /events for Timeline's deeper
      // pagination path; Viewing Deck prioritizes demo-friendly responsiveness.
      void runId
      container.innerHTML = renderEventList(_liveEvents, _activeFilter)
    }
  }

  filtersContainer.addEventListener('click', (e) => {
    const pill = (e.target as Element).closest('.fp[data-obs-filter]')
    if (pill instanceof HTMLElement) {
      handlePillActivate(pill)
    }
  })

  filtersContainer.addEventListener('keydown', (e: Event) => {
    const ke = e as KeyboardEvent
    if (ke.key !== 'Enter' && ke.key !== ' ') {
      return
    }
    const pill = (ke.target as Element).closest('.fp[data-obs-filter]')
    if (!(pill instanceof HTMLElement)) {
      return
    }
    ke.preventDefault()
    handlePillActivate(pill)
  })
}
